// ✅ ALTERED: TopicPage UI to match your portal theme (Orange header + blue borders)
// - Uses MUI components (Paper/Card-like surfaces) instead of raw HTML
// - Orange gradient header bar
// - Body BG = #F2F2F2
// - Topic cards: white surface + blue outline + left orange accent stripe + zebra tint + hover
// - Inputs: consistent outlined borders + focus orange
// - Buttons: theme orange primary + outlined blue secondary
// NOTE: API/logic unchanged (only UI + small UX polish)

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  Divider,
  Chip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const TopicPage = ({ classId }) => {
  // ✅ Theme constants (same as your other pages)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const SURFACE_BORDER = "rgba(25,118,210,0.25)";
  const ROW_ALT = "rgba(25,118,210,0.06)";

  const [topics, setTopics] = useState([]);
  const [newTopic, setNewTopic] = useState({
    Title: "",
    Content: "",
    YoutubeLink: "",
  });

  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  // Fetch topics for this class
  useEffect(() => {
    if (!classId) return;
    setErr("");
    axios
      .get(`/api/topics/${classId}`)
      .then((res) => setTopics(res.data || []))
      .catch(() => setErr("Error fetching topics"));
  }, [classId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewTopic((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");

    axios
      .post("/api/topics", { ClassID: classId, ...newTopic })
      .then((res) => {
        setTopics((prev) => [
          ...prev,
          { TopicID: res.data?.TopicID || Date.now(), ...newTopic },
        ]);
        setNewTopic({ Title: "", Content: "", YoutubeLink: "" });
        setMsg("Topic added successfully.");
      })
      .catch(() => setErr("Error creating topic"));
  };

  // ✅ common sx
  const surfacePaperSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${SURFACE_BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: SURFACE_BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(25,118,210,0.45)" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(240,120,40,0.75)",
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": { borderColor: "rgba(25,118,210,0.55)", backgroundColor: "rgba(25,118,210,0.04)" },
  };

  const topicCardSx = (i) => ({
    ...surfacePaperSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    backgroundColor: i % 2 ? ROW_ALT : "white",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
    "&:hover": { backgroundColor: "rgba(240,120,40,0.04)" },
  });

  return (
    <Box sx={{ p: 2, backgroundColor: BG }}>
      <Paper
        elevation={5}
        sx={{
          mx: "auto",
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid rgba(25,118,210,0.15)",
        }}
      >
        {/* ✅ Header bar */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, lineHeight: 1.2 }}>
              Topics
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Add and view topics for this class
            </Typography>
          </Box>
        </Box>

        {/* ✅ Body */}
        <Box sx={{ p: 2, backgroundColor: BG }}>
          {msg && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {msg}
            </Alert>
          )}
          {err && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {err}
            </Alert>
          )}

          {/* Add New Topic */}
          <Paper variant="outlined" sx={{ ...surfacePaperSx, p: 2, mb: 2 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Add New Topic
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  name="Title"
                  label="Title"
                  value={newTopic.Title}
                  onChange={handleChange}
                  required
                  fullWidth
                  size="small"
                  sx={inputSx}
                />

                <TextField
                  name="Content"
                  label="Content"
                  value={newTopic.Content}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  multiline
                  minRows={3}
                  sx={inputSx}
                />

                <TextField
                  name="YoutubeLink"
                  label="YouTube Link (optional)"
                  value={newTopic.YoutubeLink}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    sx={outlinedBtnSx}
                    onClick={() => setNewTopic({ Title: "", Content: "", YoutubeLink: "" })}
                  >
                    Clear
                  </Button>
                  <Button type="submit" variant="contained" sx={primaryBtnSx}>
                    Add Topic
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Paper>

          {/* Topics list */}
          <Paper variant="outlined" sx={{ ...surfacePaperSx, p: 2 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Topic List
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

            {!topics.length ? (
              <Typography sx={{ opacity: 0.75 }}>No topics added yet.</Typography>
            ) : (
              <Stack spacing={2}>
                {topics.map((topic, i) => (
                  <Paper key={topic.TopicID} variant="outlined" sx={topicCardSx(i)}>
                    <Box sx={{ pl: 1 }}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        justifyContent="space-between"
                      >
                        <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                          {topic.Title}
                        </Typography>

                        {topic.YoutubeLink ? (
                          <Button
                            component="a"
                            href={topic.YoutubeLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            variant="outlined"
                            endIcon={<OpenInNewIcon />}
                            sx={outlinedBtnSx}
                          >
                            Watch
                          </Button>
                        ) : null}
                      </Stack>

                      {topic.Content ? (
                        <Typography sx={{ mt: 1, opacity: 0.85, whiteSpace: "pre-wrap" }}>
                          {topic.Content}
                        </Typography>
                      ) : (
                        <Typography sx={{ mt: 1, opacity: 0.6 }}>
                          (No description)
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default TopicPage;
